import { useMutation, type UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { newIdempotencyKey, type RequestOptions } from '@/api/client';
import type { ApiError } from '@/api/errors';
import { isApiError, isSessionGateRefusal } from '@/api/errors';
import { applyWriteEffects } from '@/api/invalidation';
import type { AnyRoute, ResponseOf } from '@/api/routes';
import { useApiClient, useSessionContext } from '@/features/auth/session';

/**
 * Every write this application makes, with an idempotency key attached and a 409 that surfaces
 * rather than disappearing.
 *
 * **The key belongs to the SUBMISSION, not to the attempt** (AD-30). It is minted once, when the
 * operator presses the button, and kept for as long as that submission is being retried — so a
 * timeout the operator retries is answered with the ORIGINAL response rather than writing a second
 * record, and two deliberate presses of the button are two keys and two writes. A client that minted
 * a key per HTTP request would satisfy the header and protect nothing at all, which is exactly the
 * failure this is written to avoid.
 *
 * That is why the key is part of the mutation's VARIABLES: TanStack Query hands the same variables
 * object to every retry of one `mutate` call, so the key survives a retry by construction rather
 * than by a ref somebody has to remember to reset.
 *
 * **A conflict is a first-class outcome.** A 409 means somebody else changed the record between the
 * read and the write; the version guard caught it and nothing was saved. The mutation exposes it as
 * {@link ApiMutation.conflict} so the caller can open the field-level dialog, and it never retries
 * and never resubmits: a client that resolved the conflict by sending its own values again is a
 * client that silently overwrote somebody's work, which is the one outcome this whole mechanism
 * exists to prevent.
 */

/** The 409 codes this product uses for a lost-update conflict. */
const CONFLICT_CODES = new Set([
  'PERSON_VERSION_CONFLICT',
  'CREDENTIAL_VERSION_CONFLICT',
  'FIELD_VERSION_CONFLICT',
  'AUTH_USER_CONFLICT',
]);

/** Whether a thrown value is somebody else having saved first. */
function isVersionConflict(error: unknown): error is ApiError {
  return isApiError(error) && CONFLICT_CODES.has(error.code);
}

/** What `useApiMutation` gives a screen. */
export interface ApiMutation<TRoute extends AnyRoute, TInput> {
  readonly submit: (input: TInput) => void;
  readonly submitting: boolean;
  readonly result: ResponseOf<TRoute> | undefined;
  readonly error: unknown;
  /** The refusal that says somebody saved first, or `null`. Cleared by {@link clearConflict}. */
  readonly conflict: ApiError | null;
  readonly clearConflict: () => void;
  /**
   * Forgets the last outcome, so a screen that keeps this mutation mounted stops rendering it.
   *
   * TanStack clears a mutation's `error` only on its own next `pending` dispatch, which means a
   * refusal outlives the thing it describes until somebody presses the button again. This is the
   * way out for a caller that knows the refusal no longer applies. The lock is handled here rather
   * than by each caller — see the effect below.
   */
  readonly reset: () => void;
  /** The key this submission is carrying, so a retry of it is a replay rather than a second write. */
  readonly idempotencyKey: string | null;
}

interface Variables<TInput> {
  readonly input: TInput;
  readonly idempotencyKey: string;
}

/**
 * Wraps one route in a mutation.
 *
 * `build` turns the caller's own input into the route's request options, which is what keeps a
 * screen talking in its own terms — a person, a PIN, a credential — rather than in the client's.
 */
export function useApiMutation<TRoute extends AnyRoute, TInput>(
  route: TRoute,
  build: (input: TInput) => Omit<RequestOptions<TRoute>, 'idempotencyKey'>,
  options?: {
    readonly onSuccess?: (result: ResponseOf<TRoute>) => void;
    /** For a caller holding state that has to be released whichever way the write went. */
    readonly onError?: (error: unknown) => void;
  },
): ApiMutation<TRoute, TInput> {
  const client = useApiClient();
  const queries = useQueryClient();
  const [conflict, setConflict] = useState<ApiError | null>(null);
  const [key, setKey] = useState<string | null>(null);
  /**
   * The key of a submission that has NOT succeeded yet, with the input it was minted for.
   *
   * This is what makes the key belong to the submission rather than to the attempt. A retry after a
   * failure is the same submission and reuses it; a submission of different input is a different
   * one and mints a new key; and a key is forgotten the moment its submission succeeds, so two
   * deliberate presses of a button that worked are two writes, which is what AD-30 asks for.
   */
  const outstanding = useRef<{ signature: string; key: string } | null>(null);

  const mutation: UseMutationResult<ResponseOf<TRoute>, unknown, Variables<TInput>> = useMutation({
    mutationFn: (variables: Variables<TInput>) =>
      client.request(route, {
        ...build(variables.input),
        idempotencyKey: variables.idempotencyKey,
      } as RequestOptions<TRoute>),
    onSuccess: (result, variables) => {
      setConflict(null);
      // Forgotten on success, so the NEXT press of the button is a second write rather than a
      // replay of the one that just worked.
      outstanding.current = null;
      options?.onSuccess?.(result);
      // **After the caller's callback, and never awaited.** This function is not `async` and must
      // not become one. The library awaits it BEFORE it dispatches success, and `person-form.tsx`
      // depends on that: it re-submits from inside its own `onSuccess` so that the Save button
      // stays disabled across the swap. A microtask anywhere in here opens the one frame in which
      // that button is enabled again, and a second press in that frame is a second person with a
      // second active pass. `applyWriteEffects` starts its invalidations and returns.
      const sent = build(variables.input) as {
        readonly params?: Readonly<Record<string, string>>;
        readonly body?: unknown;
      };
      applyWriteEffects(queries, route, {
        params: sent.params ?? {},
        body: sent.body,
        result,
      });
    },
    onError: (error: unknown) => {
      setConflict(isVersionConflict(error) ? error : null);
      options?.onError?.(error);
    },
  });

  const { mutate, reset } = mutation;

  /**
   * A refusal by the idle lock does not survive the unlock.
   *
   * `failureMessage` already stops this rendering as "enter your PIN" once the PIN has been
   * accepted, but a reworded sentence is still a sentence: the operator came back to a form that
   * was never saved and there is nothing useful left to say about an interruption that is over.
   * Clearing it here covers all twenty-nine call sites at once — including the registration form,
   * where the alternative was a red banner under a half-filled record for as long as the screen
   * stayed open.
   *
   * **Only a session-gate refusal.** A refusal the operator has to read — a duplicate passport, a
   * validation failure — is theirs and stays on screen across a lock, because coming back to a form
   * with the explanation silently removed is worse than coming back to it intact.
   */
  const { state } = useSessionContext();
  const active = state.status === 'active';
  const heldError = mutation.error;
  useEffect(() => {
    if (active && isSessionGateRefusal(heldError)) {
      reset();
    }
  }, [active, heldError, reset]);

  const submit = useCallback(
    (input: TInput) => {
      // The operator pressing the button again after a failure is THE SAME SUBMISSION. That is the
      // only retry path this application has — `retry: false` on every mutation means the library
      // never re-issues one — so a key minted per press would leave the one case AD-30 exists for
      // completely unprotected: a create that committed and whose response was lost becomes a
      // second person with a second active pass when the operator presses Save again.
      //
      // **The signature is `JSON.stringify`, and a `File` in the input serialises to `{}`.** That
      // is a real limit rather than an oversight: two different files chosen under the same label
      // produce the same signature, so a retry after a failure would reuse the first key. It costs
      // nothing today because neither route that carries a file — `credential_batch.import` and
      // `person.image_upload` — is behind the idempotency middleware, so the header is inert on
      // both; the day one of them is mounted there, this needs the file's name and size in the
      // signature, and a comment is cheaper than the bug that finds it.
      const signature = JSON.stringify(input);
      const held = outstanding.current;
      const idempotencyKey =
        held !== null && held.signature === signature ? held.key : newIdempotencyKey();

      outstanding.current = { signature, key: idempotencyKey };
      setKey(idempotencyKey);
      mutate({ input, idempotencyKey });
    },
    [mutate],
  );

  return {
    submit,
    submitting: mutation.isPending,
    result: mutation.data,
    error: mutation.error,
    conflict,
    clearConflict: useCallback(() => {
      setConflict(null);
    }, []),
    reset,
    idempotencyKey: key,
  };
}
